import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { API } from '../../contexts/AuthContext';
import ModalBase from '../ModalBase';
import { formatarCodigo, fmtNum, nomeProduto } from '../../helpers/estoque';

// Modais do Estoque:
//   ModalProduto      cadastro/edicao (codigo automatico, checagem de duplicado)
//   ModalMovimentar   entrada, reposicao, saidas, devolucao, ajuste
//   ModalRetorno      fecha uma saida para manutencao (usada + devolvida)

const lerNum = (v) => {
  if (v === null || v === undefined || String(v).trim() === '') return null;
  const n = Number(String(v).replace(',', '.'));
  return Number.isFinite(n) ? n : NaN;
};

function Erro({ texto }) {
  if (!texto) return null;
  return <div className="feedback-banner feedback-banner--error" style={{ marginTop: 12, whiteSpace: 'pre-line' }}>{texto}</div>;
}

const semAcento = (t) => String(t || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();

export function ModalProduto({ produto, produtos = [], categorias, marcas, categoriaInicial, subcategoriaInicial, onClose, onSalvo, onMarcaCriada, onCategoriaCriada, onAdicionarEstoque }) {
  const editando = !!produto;
  const [form, setForm] = useState({
    categoria_id: produto?.categoria_id || categoriaInicial || '',
    subcategoria_id: produto?.subcategoria_id || subcategoriaInicial || '',
    descricao: produto?.descricao || '',
    marca_id: produto?.marca_id || '',
    modelo: produto?.modelo || '',
    detalhe: produto?.detalhe || '',
    unidade: produto?.unidade || 'un',
    quantidade_inicial: '',
    estoque_minimo: produto ? String(Number(produto.estoque_minimo)) : '',
    observacoes: produto?.observacoes || '',
    status: produto?.status || 'ativo',
  });
  const [novaMarca, setNovaMarca] = useState(null); // null | string
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState('');
  const [duplicado, setDuplicado] = useState(null);

  const set = (k, v) => { setForm((f) => ({ ...f, [k]: v })); setDuplicado(null); };

  const principais = categorias.filter((c) => !c.parent_id && (c.ativo || c.id === form.categoria_id));
  const subs = categorias.filter((c) => c.parent_id && c.parent_id === Number(form.categoria_id) && (c.ativo || c.id === form.subcategoria_id));
  const categoria = categorias.find((c) => c.id === Number(form.categoria_id));
  const pedeMarca = categoria ? !!categoria.pede_marca_modelo : true;

  // Ao digitar o nome no cadastro, mostra os produtos ja existentes com nome
  // parecido para repor em vez de criar outro codigo.
  const nomeDigitado = semAcento(form.descricao);
  const parecidos = !editando && nomeDigitado.length >= 2
    ? produtos.filter((p) => p.status === 'ativo' && semAcento(p.descricao).includes(nomeDigitado)).slice(0, 5)
    : [];

  const criarCategoria = async (parent) => {
    const nome = window.prompt(parent ? `Nova subcategoria em ${parent.nome}:` : 'Nova categoria:');
    if (!nome || !nome.trim()) return;
    try {
      const res = await axios.post(`${API}/insumos/categorias`, { nome: nome.trim(), parent_id: parent?.id || null });
      onCategoriaCriada?.(res.data.data);
      if (parent) set('subcategoria_id', res.data.data.id);
      else { set('categoria_id', res.data.data.id); set('subcategoria_id', ''); }
    } catch (err) {
      setErro(err.response?.data?.message || 'Falha ao criar a categoria.');
    }
  };

  const criarMarca = async () => {
    const nome = (novaMarca || '').trim();
    if (!nome) return;
    try {
      const res = await axios.post(`${API}/insumos/marcas`, { nome });
      onMarcaCriada?.(res.data.data);
      set('marca_id', res.data.data.id);
      setNovaMarca(null);
    } catch (err) {
      setErro(err.response?.data?.message || 'Falha ao cadastrar a marca.');
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setErro('');
    if (!form.categoria_id) { setErro('Selecione a categoria.'); return; }
    if (form.descricao.trim().length < 2) { setErro('Informe o nome do produto.'); return; }
    const qtd = lerNum(form.quantidade_inicial);
    const minimo = lerNum(form.estoque_minimo);
    if (Number.isNaN(qtd) || qtd < 0) { setErro('Quantidade inicial inválida.'); return; }
    if (Number.isNaN(minimo) || minimo < 0) { setErro('Estoque mínimo inválido.'); return; }

    const payload = {
      ...form,
      categoria_id: Number(form.categoria_id),
      subcategoria_id: form.subcategoria_id ? Number(form.subcategoria_id) : null,
      marca_id: pedeMarca && form.marca_id ? Number(form.marca_id) : null,
      modelo: pedeMarca ? form.modelo : '',
      quantidade_inicial: qtd || 0,
      estoque_minimo: minimo || 0,
    };
    setSalvando(true);
    try {
      const res = editando
        ? await axios.put(`${API}/insumos/${produto.id}`, payload)
        : await axios.post(`${API}/insumos`, payload);
      onSalvo(res.data.data, !editando);
    } catch (err) {
      if (err.response?.status === 409 && err.response.data?.data?.produto_existente) {
        setDuplicado(err.response.data.data.produto_existente);
      } else {
        setErro(err.response?.data?.message || 'Falha ao salvar o produto.');
      }
    } finally {
      setSalvando(false);
    }
  };

  return (
    <ModalBase titulo={editando ? `Produto ${formatarCodigo(produto.numero)}` : 'Cadastrar novo produto'} onClose={onClose} largura={680}>
      <form onSubmit={submit} className="portal-form">
        {!editando ? (
          <p className="content-card__hint" style={{ marginTop: 0 }}>
            O código é gerado automaticamente e fica com o produto para sempre — reposições não geram código novo.
          </p>
        ) : null}
        <div className="portal-form-grid">
          <label className="portal-field">
            <span>Categoria *</span>
            <select
              value={form.categoria_id}
              onChange={(e) => {
                if (e.target.value === '__nova__') { criarCategoria(null); return; }
                set('categoria_id', e.target.value); set('subcategoria_id', '');
              }}
            >
              <option value="">Selecione...</option>
              {principais.map((c) => <option key={c.id} value={c.id}>{c.nome}</option>)}
              <option value="__nova__">+ Nova categoria</option>
            </select>
          </label>
          <label className="portal-field">
            <span>Subcategoria</span>
            <select
              value={form.subcategoria_id}
              disabled={!categoria}
              onChange={(e) => (e.target.value === '__nova__' ? criarCategoria(categoria) : set('subcategoria_id', e.target.value))}
            >
              <option value="">Nenhuma</option>
              {subs.map((c) => <option key={c.id} value={c.id}>{c.nome}</option>)}
              {categoria ? <option value="__nova__">+ Nova subcategoria</option> : null}
            </select>
          </label>
          <label className="portal-field">
            <span>Nome do produto *</span>
            <input value={form.descricao} maxLength={255} onChange={(e) => set('descricao', e.target.value)} placeholder="Ex.: Mouse" />
          </label>
          <label className="portal-field">
            <span>Unidade de medida</span>
            <input value={form.unidade} maxLength={20} onChange={(e) => set('unidade', e.target.value)} placeholder="un, m, cx..." />
          </label>
          {pedeMarca ? (
            <>
              <div className="portal-field">
                <span>Marca</span>
                {novaMarca === null ? (
                  <select
                    value={form.marca_id}
                    onChange={(e) => (e.target.value === '__nova__' ? setNovaMarca('') : set('marca_id', e.target.value))}
                  >
                    <option value="">Sem marca</option>
                    {marcas.map((m) => <option key={m.id} value={m.id}>{m.nome}</option>)}
                    <option value="__nova__">+ Cadastrar nova marca</option>
                  </select>
                ) : (
                  <div style={{ display: 'flex', gap: 8 }}>
                    <input
                      autoFocus value={novaMarca} maxLength={100} placeholder="Nome da marca"
                      onChange={(e) => setNovaMarca(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); criarMarca(); } }}
                    />
                    <button type="button" className="btn-portal btn-portal--primary" onClick={criarMarca}>OK</button>
                    <button type="button" className="btn-portal btn-portal--outline" onClick={() => setNovaMarca(null)}>
                      <i className="bi bi-x" />
                    </button>
                  </div>
                )}
              </div>
              <label className="portal-field">
                <span>Modelo</span>
                <input value={form.modelo} maxLength={100} onChange={(e) => set('modelo', e.target.value)} placeholder="Ex.: M170" />
              </label>
            </>
          ) : null}
          {!editando ? (
            <label className="portal-field">
              <span>Quantidade inicial</span>
              <input inputMode="decimal" value={form.quantidade_inicial} onChange={(e) => set('quantidade_inicial', e.target.value)} placeholder="0" />
            </label>
          ) : (
            <div className="portal-field">
              <span>Estoque atual</span>
              <input value={`${fmtNum(produto.estoque_atual)} ${produto.unidade}`} disabled />
            </div>
          )}
          <label className="portal-field">
            <span>Estoque mínimo</span>
            <input inputMode="decimal" value={form.estoque_minimo} onChange={(e) => set('estoque_minimo', e.target.value)} placeholder="0" />
          </label>
          {editando ? (
            <label className="portal-field">
              <span>Status</span>
              <select value={form.status} onChange={(e) => set('status', e.target.value)}>
                <option value="ativo">Ativo</option>
                <option value="inativo">Inativo</option>
                <option value="descontinuado">Descontinuado</option>
              </select>
            </label>
          ) : null}
        </div>
        <label className="portal-field" style={{ marginTop: 12 }}>
          <span>Descrição</span>
          <input value={form.detalhe} maxLength={500} onChange={(e) => set('detalhe', e.target.value)} />
        </label>
        <label className="portal-field" style={{ marginTop: 12 }}>
          <span>Observações</span>
          <textarea rows={2} value={form.observacoes} onChange={(e) => set('observacoes', e.target.value)} />
        </label>
        {editando ? (
          <p className="content-card__hint">O saldo só muda por movimentação (botão Movimentar), nunca pelo cadastro.</p>
        ) : null}

        {parecidos.length && !duplicado ? (
          <div className="feedback-banner feedback-banner--info" style={{ marginTop: 12 }}>
            <strong>Já cadastrado com nome parecido</strong> — para aumentar a quantidade, não cadastre de novo:
            <ul className="estoque-parecidos">
              {parecidos.map((p) => (
                <li key={p.id}>
                  <span>
                    <strong>{formatarCodigo(p.numero)}</strong> {nomeProduto(p)}
                    <span className="cell-muted"> · {fmtNum(p.estoque_atual)} {p.unidade}</span>
                  </span>
                  <button type="button" className="btn-portal btn-portal--outline" onClick={() => onAdicionarEstoque(p)}>
                    <i className="bi bi-plus-lg" /> Adicionar quantidade
                  </button>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
        {duplicado ? (
          <div className="feedback-banner feedback-banner--warning" style={{ marginTop: 12 }}>
            <strong>Este produto já existe:</strong> {nomeProduto(duplicado)} — código {formatarCodigo(duplicado.numero)}
            {duplicado.status !== 'ativo' ? ` (${duplicado.status})` : ''}, estoque {fmtNum(duplicado.estoque_atual)}.
            {!editando && duplicado.status === 'ativo' ? (
              <div style={{ marginTop: 8 }}>
                <button type="button" className="btn-portal btn-portal--primary" onClick={() => onAdicionarEstoque(duplicado)}>
                  <i className="bi bi-plus-circle" /> Adicionar estoque / Repor quantidade
                </button>
              </div>
            ) : null}
          </div>
        ) : null}
        <Erro texto={erro} />

        <div className="portal-form-actions" style={{ justifyContent: 'flex-end', marginTop: 16 }}>
          <button type="button" className="btn-portal btn-portal--outline" onClick={onClose}>Cancelar</button>
          <button type="submit" className="btn-portal btn-portal--primary" disabled={salvando}>
            {salvando ? 'Salvando...' : editando ? 'Salvar alterações' : 'Cadastrar produto'}
          </button>
        </div>
      </form>
    </ModalBase>
  );
}

// `simples`: so "Adicionar quantidade" (entrada por reposicao), sem escolher tipo.
export function ModalMovimentar({ produto, tipos, tipoInicial = 'entrada', simples = false, onClose, onSalvo }) {
  const opcoes = useMemo(
    () => tipos.filter((t) => !t.interno && (produto.status === 'ativo' || ['devolucao', 'ajuste'].includes(t.chave))),
    [tipos, produto.status]
  );
  const [tipo, setTipo] = useState(opcoes.some((t) => t.chave === tipoInicial) ? tipoInicial : opcoes[0]?.chave);
  const [form, setForm] = useState({ quantidade: '', motivo: '', observacao: '', filial: '', setor: '', destino: '', referencia: '' });
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState('');
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const def = tipos.find((t) => t.chave === tipo) || {};
  const ehAjuste = def.sinal === 0;
  const atual = Number(produto.estoque_atual);
  const q = lerNum(form.quantidade);
  let novo = null;
  if (q !== null && !Number.isNaN(q)) novo = ehAjuste ? q : atual + def.sinal * q;
  const insuficiente = novo !== null && novo < 0;

  const submit = async (e) => {
    e.preventDefault();
    setErro('');
    if (q === null || Number.isNaN(q) || (ehAjuste ? q < 0 : q <= 0)) {
      setErro(ehAjuste ? 'Informe a contagem física.' : 'Informe uma quantidade maior que zero.');
      return;
    }
    if (insuficiente) { setErro('Estoque insuficiente para essa saída.'); return; }
    if (ehAjuste && novo === atual) { setErro('A contagem é igual ao estoque atual: não há o que ajustar.'); return; }
    if (ehAjuste && (!form.motivo.trim() || !form.observacao.trim())) { setErro('O ajuste exige motivo e observação.'); return; }
    if (def.exige === 'filial' && !form.filial.trim()) { setErro('Informe a filial de destino.'); return; }
    if (def.exige === 'setor' && !form.setor.trim()) { setErro('Informe o setor de destino.'); return; }
    setSalvando(true);
    try {
      const res = await axios.post(`${API}/insumos/${produto.id}/movimentar`, { ...form, tipo_movimentacao: tipo, quantidade: q });
      onSalvo(res.data.data);
    } catch (err) {
      setErro(err.response?.data?.message || 'Falha ao registrar a movimentação.');
    } finally {
      setSalvando(false);
    }
  };

  return (
    <ModalBase titulo={simples ? 'Adicionar quantidade' : 'Movimentar estoque'} onClose={onClose} largura={620}>
      <p style={{ marginTop: 0 }}>
        <strong>{nomeProduto(produto)}</strong> — código {formatarCodigo(produto.numero)}<br />
        <span className="cell-muted">Estoque atual: <strong>{fmtNum(atual)} {produto.unidade}</strong></span>
      </p>
      <form onSubmit={submit} className="portal-form">
        <div className="portal-form-grid">
          {!simples ? (
            <label className="portal-field">
              <span>Tipo de movimentação</span>
              <select value={tipo} onChange={(e) => setTipo(e.target.value)}>
                {opcoes.map((t) => <option key={t.chave} value={t.chave}>{t.label}</option>)}
              </select>
            </label>
          ) : null}
          <label className="portal-field">
            <span>{ehAjuste ? 'Contagem física' : simples ? 'Quantidade a adicionar' : 'Quantidade'}</span>
            <input autoFocus inputMode="decimal" value={form.quantidade} onChange={(e) => set('quantidade', e.target.value)} />
          </label>
          {def.exige === 'filial' || def.retorno ? (
            <label className="portal-field">
              <span>Filial / loja{def.exige === 'filial' ? ' *' : ''}</span>
              <input value={form.filial} maxLength={100} onChange={(e) => set('filial', e.target.value)} />
            </label>
          ) : null}
          {def.exige === 'setor' ? (
            <label className="portal-field">
              <span>Setor *</span>
              <input value={form.setor} maxLength={100} onChange={(e) => set('setor', e.target.value)} />
            </label>
          ) : null}
          {def.sinal === -1 ? (
            <label className="portal-field">
              <span>Destino / finalidade</span>
              <input value={form.destino} maxLength={255} onChange={(e) => set('destino', e.target.value)} placeholder="Ex.: manutenção do CFTV" />
            </label>
          ) : null}
          <label className="portal-field">
            <span>Motivo{ehAjuste ? ' *' : ''}</span>
            <input value={form.motivo} maxLength={255} onChange={(e) => set('motivo', e.target.value)}
              placeholder={def.sinal === 1 ? 'Ex.: compra, NF 1234' : ''} />
          </label>
          <label className="portal-field">
            <span>Referência</span>
            <input value={form.referencia} maxLength={100} onChange={(e) => set('referencia', e.target.value)} placeholder="Nº chamado, NF, pedido..." />
          </label>
        </div>
        <label className="portal-field" style={{ marginTop: 12 }}>
          <span>Observação{ehAjuste ? ' *' : ''}</span>
          <textarea rows={2} value={form.observacao} onChange={(e) => set('observacao', e.target.value)} />
        </label>
        {def.retorno ? (
          <p className="content-card__hint">Na volta, registre o que foi utilizado e o que foi devolvido em "Retornos pendentes".</p>
        ) : null}
        {novo !== null && !Number.isNaN(novo) ? (
          <p className={insuficiente ? 'insumo-ck__alerta' : 'cell-muted'}>
            Saldo: {fmtNum(atual)} → <strong>{fmtNum(novo)}</strong>
            {ehAjuste && novo !== atual ? ` (ajuste de ${novo > atual ? '+' : ''}${fmtNum(novo - atual)})` : ''}
            {insuficiente ? ` — estoque insuficiente (déficit ${fmtNum(-novo)})` : ''}
          </p>
        ) : null}
        <Erro texto={erro} />
        <div className="portal-form-actions" style={{ justifyContent: 'flex-end', marginTop: 16 }}>
          <button type="button" className="btn-portal btn-portal--outline" onClick={onClose}>Cancelar</button>
          <button type="submit" className="btn-portal btn-portal--primary" disabled={salvando || insuficiente}>
            {salvando ? 'Registrando...' : 'Registrar'}
          </button>
        </div>
      </form>
    </ModalBase>
  );
}

export function ModalRetorno({ saida, onClose, onSalvo }) {
  const [usada, setUsada] = useState('');
  const [devolvida, setDevolvida] = useState('');
  const [observacao, setObservacao] = useState('');
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState('');

  const retirada = Number(saida.quantidade);
  const u = lerNum(usada);
  const d = lerNum(devolvida);
  const preenchido = u !== null && d !== null && !Number.isNaN(u) && !Number.isNaN(d);
  const confere = preenchido && Math.round((u + d) * 100) === Math.round(retirada * 100);

  useEffect(() => { setErro(''); }, [usada, devolvida]);

  const submit = async (e) => {
    e.preventDefault();
    if (!confere) return;
    setSalvando(true);
    try {
      await axios.post(`${API}/insumos/movimentacoes/${saida.id}/retorno`, { qtd_usada: u, qtd_devolvida: d, observacao });
      onSalvo();
    } catch (err) {
      setErro(err.response?.data?.message || 'Falha ao registrar o retorno.');
    } finally {
      setSalvando(false);
    }
  };

  return (
    <ModalBase titulo="Registrar retorno da manutenção" onClose={onClose} largura={540}>
      <p style={{ marginTop: 0 }}>
        <strong>{saida.produto}</strong> — código {formatarCodigo(saida.numero)}<br />
        <span className="cell-muted">
          Retirado: <strong>{fmtNum(retirada)}</strong> em {new Date(saida.created_at).toLocaleString('pt-BR')}
          {saida.autor ? ` por ${saida.autor}` : ''}{saida.filial ? ` · ${saida.filial}` : ''}
        </span>
      </p>
      <form onSubmit={submit} className="portal-form">
        <div className="portal-form-grid">
          <label className="portal-field">
            <span>Quantidade utilizada</span>
            <input autoFocus inputMode="decimal" value={usada} onChange={(e) => setUsada(e.target.value)} />
          </label>
          <label className="portal-field">
            <span>Quantidade devolvida</span>
            <input inputMode="decimal" value={devolvida} onChange={(e) => setDevolvida(e.target.value)} />
          </label>
        </div>
        <label className="portal-field" style={{ marginTop: 12 }}>
          <span>Observação</span>
          <input value={observacao} onChange={(e) => setObservacao(e.target.value)} />
        </label>
        {preenchido ? (
          confere ? (
            <p className="insumo-ck__ok">✅ Quantidades conferem: {fmtNum(retirada)} = {fmtNum(u)} + {fmtNum(d)}. Volta ao estoque: {fmtNum(d)}.</p>
          ) : (
            <p className="insumo-ck__alerta">
              ⚠️ Não confere: retirado {fmtNum(retirada)}, informado {fmtNum(u + d)} (diferença de {fmtNum(Math.abs(retirada - u - d))}).
            </p>
          )
        ) : null}
        <Erro texto={erro} />
        <div className="portal-form-actions" style={{ justifyContent: 'flex-end', marginTop: 16 }}>
          <button type="button" className="btn-portal btn-portal--outline" onClick={onClose}>Cancelar</button>
          <button type="submit" className="btn-portal btn-portal--primary" disabled={salvando || !confere}>
            {salvando ? 'Registrando...' : 'Registrar retorno'}
          </button>
        </div>
      </form>
    </ModalBase>
  );
}
